import Store from "./store/store";
import {wait, getCookie} from "./index";
import {shouldAutoRetryStatus, transportErrorKind} from "../model/client_events.mjs";

class API extends Store {
  error_fetch = null
  get_url = ''
  post_url = ''
  patch_url = ''
  delete_url = ''
  dataListKey = null
  dataDetailKey = null

  constructor(ctx, urls = {}) {
    super(ctx);
    this.get_url = urls.get
    this.post_url = urls.post
    this.patch_url = urls.patch
    this.delete_url = urls.delete
    this.detail_url = urls.detail
  }

  preSave(body) {
    return body
  }

  // Что реально уезжает НА СЕРВЕР. Отделено от `preSave` намеренно: `preSave` даёт то, во что
  // клиент ВЕРИТ (и что рисует оптимистично), а это — то, что сервер обязан получить. Пока они
  // совпадали, различать было нечего; с event-native клиент перестаёт слать доску, но продолжает
  // её рисовать, и склей мы эти два смысла — оптимистичный рендер исчез бы вместе с отправкой.
  toWire(body) {
    return body
  }

  optimisticUpdate() {
  }

  optimisticDelete() {
  }

  afterFetch() {
  }

  afterSave() {
  }

  afterDelete() {
  }


  async fetch(query = {}, force = false) {
    let url = this.get_url;
    if (Object.keys(query).length > 0) {
      const filteredQuery = Object.fromEntries(
        Object.entries(query).filter(([_, value]) => value !== null && value !== undefined)
      );
      if (Object.keys(filteredQuery).length > 0) {
        const params = new URLSearchParams(filteredQuery);
        url += `?${params.toString()}`;
      }
    }
    if (force) {
      this.set('status', 'loading');
    }
    const data = await this.request({
      url: url,
      method: 'GET',
      after: (d) => this.afterFetch(d)
    })
    this.afterFetch(data)
    return data
  }

  // ⚑ УСТАРЕВШИЙ ОТВЕТ НЕ ЗАТИРАЕТ БОЛЕЕ СВЕЖЕЕ СОСТОЯНИЕ (жалоба владельца 02.08.2026: «второе
  // число исчезает, когда приходит первый ответ, и появляется, когда приходит второй»).
  //
  // Сейвы серии идут по очереди, а `afterSave` ЗАМЕНЯЕТ стор телом ответа целиком. Ответ на первый
  // сейв про второй подход не знает — значит, применённый после того, как второй уже отправлен, он
  // откатывает экран назад во времени. Игрок видит, как подход исчезает и через круг возвращается.
  // Данные при этом не терялись (последний ответ приносил всё), но объяснить это игроку нечем.
  //
  // Правило: применяем ответ, ТОЛЬКО если после него мы не успели отправить ещё один сейв. Считаем
  // по СВОЕМУ счётчику сейвов, а не по длине очереди запросов: в ту же очередь ходит `getVideoToken`
  // (мимо `request`, не трогая статус), и по ней «свежесть» определялась бы неверно — ответ сейва
  // отбрасывался бы из-за постороннего запроса, а второго `afterSave` уже не будет.
  _saveSeq = 0

  async save(body) {
    // `merged` — во что клиент верит СЕЙЧАС; его и рисуем оптимистично. На сервер уходит `toWire`.
    const merged = this.preSave(body)
    const seq = ++this._saveSeq
    const data = await this.request({
      url: this.post_url.replace('{id}', merged.id),
      method: 'POST',
      body: this.toWire(merged),
      optimistic: () => this.optimisticUpdate(merged),
      after: (d) => this.afterSave(d)
    })
    if (seq === this._saveSeq) this.afterSave(data)
    return data
  }

  async savePartial(body){
    const merged = this.preSave(body)
    const seq = ++this._saveSeq
    const data = await this.request({
      url: this.patch_url.replace('{id}', merged.id),
      method: 'PATCH',
      body: this.toWire(merged),
      optimistic: () => this.optimisticUpdate(merged),
      after: (d) => this.afterSave(d)
    })
    if (seq === this._saveSeq) this.afterSave(data)
    return data
  }

  async delete(id) {
    const data = await this.request({
      url: this.delete_url.replace('{id}', id),
      method: 'DELETE',
      getData: () => id,
      optimistic: () => this.optimisticDelete(id),
      after: (d) => this.afterDelete(d)
    })
    this.afterDelete(data)
    return data
  }

  // ⚑ ЗАПРОС ЗАПОМИНАЕТ САМ СЕБЯ, чтобы «Повторить» действительно повторяло (ревью, промпт 5).
  // Раньше `error_fetch` заполнялся ТОЛЬКО в `fetchRetry`, то есть при сетевом сбое. Отказ сервера
  // (любой не-200) ставил `status = 'error'`, показывал модалку — и кнопка в ней НИЧЕГО не делала:
  // `retryError` выходил на пустом `error_fetch`, а ввод при `status !== 'stable'` заморожен.
  // Вкладка замирала до перезагрузки. С 409 промпта 1 это уже было так, просто некому было заметить.
  async request({
                   url,
                   method,
                   body,
                   optimistic,
                   getData,
                   after
                 }) {
    if (this.requestQueue.queue.length === 0) {
      optimistic && optimistic(body)
    } else {
      this.set('status', 'loading')
    }
    const init = {
      method: method,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest",
        'X-CSRFToken': getCookie('csrftoken')
      },
      body: body ? JSON.stringify(body) : null,
    }
    // Дескриптор повтора — ТО ЖЕ САМОЕ тело с теми же `_eid`. Повтор безопасен ровно поэтому:
    // сервер дедупит события по `idempotency_key`, пришедшему от клиента.
    const retryCtx = {url, init, getData, after}
    let response
    try {
      response = await this.requestQueue(() => this.fetchRetry(url, init))
    } catch (e) {
      // Сетевой сбой: ИСХОД НЕИЗВЕСТЕН (запрос мог дойти и примениться). Повторяем тем же телом.
      this.error_fetch = retryCtx
      this.set('error_kind', 'retry')
      throw e
    }
    return await this.responseFinish(response, getData, retryCtx)
  }

  requestQueue = (() => {
    const run = (i = 0) => {
      return call_fetch.queue[i]().then(() => {
        if (call_fetch.queue.length !== i + 1) {
          return run(i + 1)
        }
        call_fetch.queue = []
      })
    }

    const call_fetch = (request) => {
      return new Promise((resolve, reject) => {
        call_fetch.queue.push(() => request().then(data => resolve(data)).catch(e => reject(e)))
        if (call_fetch.queue.length === 1) run()
      })
    }
    call_fetch.queue = []

    return call_fetch
  })();

  // TODO: добавить контроллер для отмены
  //
  // ⚑ `touchStatus` — «этот запрос ВПРАВЕ гасить экран партии» (правка 02.08.2026).
  // Статус экрана снимает ТОЛЬКО `responseFinish`, то есть путь через `request()`. Запрос, идущий
  // в очередь напрямую (`getVideoToken`), поднятый здесь 'loading' уже никто не снимет: экран
  // остаётся под оверлеем `loading-bg`, клавиатура мертва — до перезагрузки страницы. Поэтому
  // право трогать статус даётся ЯВНО и по умолчанию, а посторонние запросы от него отказываются.
  // Убрать работу со статусом отсюда совсем нельзя: заморозка ввода на время авто-повтора ПОДХОДА
  // нужна — иначе игрок вводит следующую клетку поверх неподтверждённой.
  async fetchRetry(url, data, retry = 3, delay = 2000, touchStatus = true) {
    try {
      const response = await fetch(url, data)
      // ⚑ ТИХИЙ АВТО-ПОВТОР НА 503 (решение владельца 01.08.2026). 503 отдаёт наш собственный
      // `views._writer_failed`: «писатель споткнулся о лок/таймаут, состояние НЕ тронуто, повтори».
      // Лок держится миллисекунды, поэтому в норме повтор проходит с первой попытки и игрок не видит
      // ничего — модалка остаётся на случай, когда не помогло. Повтор безопасен, потому что тело то
      // же и события идемпотентны по `_eid`; на прочие коды НЕ распространяем (см. `shouldAutoRetryStatus`).
      if (shouldAutoRetryStatus(response.status) && retry > 0) {
        if (touchStatus) this.set('status', 'loading');
        await wait(delay / retry)
        return await this.fetchRetry(url, data, retry - 1, delay, touchStatus)
      }
      return response
    } catch (e) {
      if (touchStatus) this.set('status', 'loading');
      if (retry > 0) {
        await wait(delay / retry)
        return await this.fetchRetry(url, data, retry - 1, delay, touchStatus)
      }
      if (touchStatus) this.set('status', 'error');
      return Promise.reject(e)
    }
  }

  // Повтор ТОГО ЖЕ запроса по кнопке модалки. Прежняя версия дёргала голый `fetchRetry` и ставила
  // 'stable' независимо от исхода: ответ не разбирался, `afterSave` не звался, стор оставался без
  // серверного состояния, а неудачный повтор выглядел успешным. Теперь повтор идёт полным путём.
  async retryError() {
    const ctx = this.error_fetch
    if (!ctx) return null
    this.set('status', 'loading');
    try {
      const response = await this.fetchRetry(ctx.url, ctx.init)
      const data = await this.responseFinish(response, ctx.getData, ctx)
      ctx.after && ctx.after(data)
      return data
    } catch (e) {
      // Статус/модалку уже выставил `responseFinish` (или `fetchRetry`). Гасим отказ здесь, чтобы
      // клик по кнопке не рождал unhandled rejection: игроку он ничего не сообщает, а во фронтовый
      // Sentry уехал бы дублем к серверному маркеру того же отказа.
      return null
    }
  }

  // Закрыть модалку отказа, ничего не повторяя (вид 'blocked' — партия заблокирована сервером).
  // Дескриптор повтора выбрасываем: повторять этот батч нельзя, а оставленный `error_fetch` позже
  // подхватила бы кнопка «Повторить» другого, уже НЕ связанного отказа.
  dismissError() {
    this.error_fetch = null
    this.set('status', 'stable');
  }

  async responseFinish(response, getData = (response) => response.json(), retryCtx = null) {
    if (response.ok) {
      const data = await getData(response)
      this.error_fetch = null
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(data)
          if (this.requestQueue.queue.length === 0) {
            this.set('status', 'stable');
          }
        }, 0)
      })
    } else {
      // Что игроку делать — решает ОДНА функция (`transportErrorKind`), а не текст модалки.
      this.set('error_kind', transportErrorKind(response.status))
      if (retryCtx) this.error_fetch = retryCtx
      this.set('status', 'error');
      // Текст ответа режем: он уезжает в сообщение ошибки и дальше во фронтовый Sentry, а `502` от
      // nginx — это целая HTML-страница.
      return Promise.reject(new Error((await response.text()).slice(0, 300)))
    }
  }
}

export default API